import type {
    GridActionsCellItemProps,
    GridColDef,
    GridKeyValue,
    GridRowId,
    GridRowModel,
    GridRowParams,
    GridRowSelectionModel
} from '@mui/x-data-grid-premium';
import type { Filter } from '@searchkit/client';
import { isEqual } from 'lodash';
import type { ReactElement } from 'react';
import { ROW_FOCUS_OUT_EDIT_ACTIONS } from '../../constants/dataGridConstants';
import type { RowFocusOutEditAction } from './types';

export const emptySelectionModel: GridRowSelectionModel = {
    ids: new Set(),
    type: 'include'
};

export const combineVariables = <BaseVariables extends object, CustomVariables extends object>(
    baseVariables: BaseVariables,
    customVariables?: CustomVariables
): BaseVariables & CustomVariables => {
    return {
        ...baseVariables,
        ...(customVariables ?? {})
    } as BaseVariables & CustomVariables;
};

function toSortKey(filter: Filter): string {
    const valuePart = filter.value ?? '';
    const rangePart = [
        filter.dateMin ?? '',
        filter.dateMax ?? '',
        filter.min ?? '',
        filter.max ?? '',
        filter.level ?? ''
    ].join('|');

    return `${filter.identifier}:${valuePart}:${rangePart}`;
}

export function sortFiltersForQuery(filters: Filter[]): Filter[] {
    return [...filters].sort((a, b) => toSortKey(a).localeCompare(toSortKey(b)));
}

export const getGroupingLabelValue = (
    value: unknown,
    column: GridColDef,
    emptyGroupValueLabel: string
): GridKeyValue => {
    const headerLabel =
        typeof column.headerName === 'string' && column.headerName.trim() !== '' ? column.headerName : column.field;

    if (value === null || value === undefined) {
        return `${headerLabel}: ${emptyGroupValueLabel}`;
    }

    if (typeof value === 'string' && value.trim() === '') {
        return `${headerLabel}: ${emptyGroupValueLabel}`;
    }

    return value as GridKeyValue;
};

export const isMissingGroupingLabel = (value: unknown, missingLabel: string): boolean => {
    return typeof value === 'string' && value.trim().endsWith(`: ${missingLabel}`);
};

const getComparableRowValue = (value: unknown) => value ?? '';

export const hasEditableRowChanges = (
    originalRow: GridRowModel,
    updatedRow: GridRowModel,
    columns: GridColDef[]
): boolean =>
    columns.some((column) => {
        if (!column.editable) {
            return false;
        }

        return !isEqual(
            getComparableRowValue(originalRow[column.field]),
            getComparableRowValue(updatedRow[column.field])
        );
    });

export const getOriginalRowForEdit = (
    id: GridRowId,
    activeRow: GridRowModel | undefined,
    rows: GridRowModel[]
): GridRowModel | undefined => {
    if (activeRow?.id === id) {
        return activeRow;
    }

    return rows.find((row) => row.id === id);
};

export const getRowFocusOutEditAction = ({
    commitOnlyWhenDirty,
    hasUnsavedChanges,
    isCommitInProgress,
    shouldSuppressCommit
}: {
    commitOnlyWhenDirty: boolean;
    hasUnsavedChanges: boolean;
    isCommitInProgress: boolean;
    shouldSuppressCommit: boolean;
}): RowFocusOutEditAction => {
    if (shouldSuppressCommit || isCommitInProgress) {
        return ROW_FOCUS_OUT_EDIT_ACTIONS.suppress;
    }

    if (commitOnlyWhenDirty && !hasUnsavedChanges) {
        return ROW_FOCUS_OUT_EDIT_ACTIONS.cancelClean;
    }

    return ROW_FOCUS_OUT_EDIT_ACTIONS.commit;
};

export function transformDataGridCustomActions(
    customActions: Array<(params: GridRowParams) => ReactElement<GridActionsCellItemProps>>,
    getColumns: () => GridColDef[]
) {
    return customActions.map((action) => (params) => action({ ...params, columns: getColumns() }));
}
